import { useCallback, useEffect, useState } from "react";
import { api, Me, Manifest, setActiveBook } from "./api";
import Login from "./views/Login";
import ChangePassword from "./views/ChangePassword";
import Layout from "./views/Layout";
import PublicPortal from "./views/PublicPortal";
import PublicStory from "./views/PublicStory";
import PublicPodcast from "./views/PublicPodcast";

// The public reading room is served at /share with NO auth. A single shared
// trade story is served at /story/<token>, also with no auth — the token is
// the only credential, which is why it's random and revocable server-side.
const path = typeof window !== "undefined" ? window.location.pathname : "";
const isPublic = path.startsWith("/share");
// /story/<slug>/<token> — the slug is human-readable padding so a pasted
// link says what it opens; the LAST segment is the only part that's checked.
const sharedToken = path.startsWith("/story/")
  ? decodeURIComponent(path.replace(/\/+$/, "").split("/").pop() || "")
  : null;
// /afterhours/<slug>/<token> — a shared episode of the podcast, no auth.
const episodeToken = path.startsWith("/afterhours/")
  ? decodeURIComponent(path.replace(/\/+$/, "").split("/").pop() || "")
  : null;

export default function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [manifest, setManifest] = useState<Manifest | null>(null);

  const refresh = useCallback(async () => {
    try {
      const m = await api.get<Me>("/api/auth/me");
      // Point every subsequent request at the user's default book BEFORE the
      // first one goes out. api.ts's module default is "paper", and Layout
      // only used to correct it from a useEffect — which runs AFTER its
      // children's effects and long after the manifest fetch below, so the
      // whole first paint (manifest + Command's equity/day-P&L) silently
      // loaded PAPER data while the book chip already read "Live · Daily"
      // (found 2026-08-04 the moment default_book stopped being "paper").
      setActiveBook(m.default_book || "live");
      setMe(m);
      if (!m.must_change_password) {
        setManifest(await api.get<Manifest>("/api/firm/manifest"));
      }
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    if (!isPublic && !sharedToken && !episodeToken) void refresh();
  }, [refresh]);

  if (episodeToken) {
    return <PublicPodcast token={episodeToken} />;
  }
  if (sharedToken) {
    return <PublicStory token={sharedToken} />;
  }
  if (isPublic) {
    return <PublicPortal />;
  }
  if (me === undefined) {
    return <div className="h-screen grid place-items-center text-slate-500">loading…</div>;
  }
  if (me === null) {
    return <Login onLogin={refresh} />;
  }
  if (me.must_change_password) {
    return <ChangePassword onDone={refresh} />;
  }
  if (!manifest) {
    return <div className="h-screen grid place-items-center text-slate-500">loading firm…</div>;
  }
  return <Layout me={me} manifest={manifest} onLogout={async () => { await api.post("/api/auth/logout"); setMe(null); }} />;
}
